import NoStockButton from "~/components/product/VariantSelector/NoStockButton"
import VariantLink, { useVariantNavigate } from "~/components/product/VariantSelector/VariantLink"
import Button from "~/components/ui/Button"
import { selectedProducts, useItemsCount, useSelectedProducts, useSubtotal } from "~/sdk/atoms"
import clsx from "~/sdk/clx"
import { formatPrice } from "@decocms/apps-commerce/sdk/formatPrice"
import { useSignalEffect } from "~/sdk/signal"
import { AggregateOffer, ImageObject, ProductLeaf, PropertyValue } from "@decocms/apps-commerce/types"
import Image from "~/components/ui/Image"
import { useOffer } from "~/sdk/useOffer"

export interface Props {
    hasVariant: ProductLeaf[]
    isCurrentProductAvailable: boolean
}

interface VariantProperty {
    name: string
    value: string
    isAvailable: boolean
    productID: string
    image: ImageObject
    quantity: number
    url?: string
}

interface Variant {
    name: string | undefined
    properties: VariantProperty[]
}

function isSpecification(property: PropertyValue) {
    return property.valueReference === 'SPECIFICATION'
}

type MapPropertyProps = {
    isAvailable: boolean
    productID: string
    image: ImageObject
    quantity: number
    url?: string
}

function mapProperty({ isAvailable, productID, image, quantity, url }: MapPropertyProps) {
    return (
        { name = 'Unknown Name', value = 'Unknown Value' }: PropertyValue,
    ): VariantProperty => ({
        name,
        value,
        isAvailable,
        productID,
        image,
        quantity,
        url,
    })
}

function isAvailable(offers: AggregateOffer) {
    return offers.offers[0]?.availability === 'https://schema.org/InStock'
}

function useVariant(leafs: ProductLeaf[]) {
    return leafs
        .map(({ name, additionalProperty = [], image: images = [], offers, productID, url }) => {
            return {
                name,
                properties: additionalProperty.filter(isSpecification).map(
                    mapProperty({
                        isAvailable: (offers && isAvailable(offers)) ?? false,
                        productID,
                        image: images?.[0] ?? { '@type': 'ImageObject' },
                        url,
                        quantity: offers?.offers[0]?.inventoryLevel.value ?? 0,
                    }),
                ),
            }
        })
        .reduce((acc, { name, properties }) => {
            const index = acc.findIndex((a) => a.name === name)
            if (index === -1) {
                acc.push({
                    name,
                    properties,
                })
            } else {
                acc[index].properties.push(...properties)
            }
            return acc
        }, [] as Variant[])
}

export default function SimpleSelector({ hasVariant, isCurrentProductAvailable }: Props) {
    // Válvula de escape do estado esgotado — ver comentário equivalente em
    // MultiSelector: com a SKU da URL sem estoque o buy box vira o formulário
    // "avise-me", então mexer na quantidade de um tamanho disponível precisa
    // navegar até ele para o CTA de compra voltar.
    const navigateToVariant = useVariantNavigate()
    const restoreBuyBox = (url?: string) => {
        if (!isCurrentProductAvailable) void navigateToVariant(url)
    }
    // Subscribe to the selectedProducts atom so that clicks on +/- actually
    // re-render this component. Without these hooks, `selectedProducts.value`
    // reads inside the render are not reactive (TanStack Store backing).
    const selected = useSelectedProducts()
    const itemsCountValue = useItemsCount()
    const subtotalValue = useSubtotal()
    // Derived from props on every render — do NOT park this in useSignal/useState:
    // the initial value would freeze on mount and an SPA navigation between PDPs
    // (same route, component instance reused) would keep rendering the previous
    // product's variants.
    const variants = useVariant(hasVariant)

    // This is a hack to reset the selected products when the user go
    // back to the product page. This is needed because some inputs
    // may be filled with the wrong quantity.
    useSignalEffect(() => {
        selectedProducts.value = new Map()
    })

    function updateProductQuantity(key: string, quantity: number) {
        if (quantity === 0) {
            selectedProducts.value.delete(key)
            selectedProducts.value = new Map(selectedProducts.value)
            return
        }

        const selectedProduct = selectedProducts.value.get(key)
        if (selectedProduct) {
            selectedProducts.value.set(key, { ...selectedProduct, quantity })
        } else {
            const product = hasVariant.find((v) => v.productID === key)

            if (!product) {
                return
            }

            const { price = 0, listPrice = 0 } = useOffer(product.offers)
            const discount = listPrice - price

            selectedProducts.value.set(key, { ...product, price, quantity, discount })
        }

        selectedProducts.value = new Map(selectedProducts.value)
    }

    return (
        <>
            <p className='text-sm max-lg:mt-2'>Selecione os itens desejados</p>
            <div className='w-full max-w-fit max-h-[400px] mt-[9px] scroll scroll-xl scroll-salmon overflow-y-auto max-lg:-order-1 isolate'>
                <ul className='text-sm border relative border-neutral-300 flex flex-col'>
                    {variants.map(({ properties }, index) => (
                        <>
                            {index === 0 && (
                                <li className='sticky top-0 z-10'>
                                    <div className='font-black gap-1 flex bg-pink-light w-full text-center border-b border-b-[#ababab]'>
                                        <p className='uppercase leading-[26px] w-20'>
                                            {properties[0].name}
                                        </p>
                                    </div>
                                </li>
                            )}
                            {properties
                                .sort((a, b) => b.quantity - a.quantity)
                                .map(
                                    ({ isAvailable, value, productID, image, url, quantity }) => {
                                        return (
                                            <li
                                                key={productID}
                                                className={`flex gap-1 border-b border-neutral-300 pt-2.5 px-1 ${
                                                    !isAvailable && 'order-1'
                                                }`}
                                            >
                                                <VariantLink
                                                    url={url}
                                                    className='flex flex-col items-center w-20 text-center py-0.5 pt-3 lg:pt-0.5 cursor-pointer hover:bg-neutral-200 transition-all'
                                                >
                                                    {image.url ? (
                                                        <Image
                                                            className='border border-neutral-300 bg-white h-[76px] w-[76px] object-contain'
                                                            width={76}
                                                            height={76}
                                                            src={image.url}
                                                            alt=''
                                                            loading='lazy'
                                                            fit='contain'
                                                        />
                                                    ) : (
                                                        <div className='border border-neutral-300 bg-white h-[76px] w-[76px]' />
                                                    )}
                                                    <p className='text-xs line-clamp-2 leading-none mt-0.5 text-black'>
                                                        {value}
                                                    </p>
                                                </VariantLink>
                                                <div className='py-0.5 pt-3 lg:pt-0.5 w-[72px]'>
                                                    {isAvailable
                                                        ? (
                                                            <div>
                                                                <div className='flex justify-center'>
                                                                    <input
                                                                        key={productID}
                                                                        id={productID}
                                                                        className={clsx(
                                                                            (selected
                                                                                    .get(
                                                                                        productID,
                                                                                    )?.quantity ??
                                                                                    0) >
                                                                                    0
                                                                                ? 'bg-[#e9ffec]'
                                                                                : 'bg-neutral-200',
                                                                            'outline-none w-[62px] text-center h-11 rounded-t border border-b-0 border-neutral-300 text-lg',
                                                                        )}
                                                                        type='number'
                                                                        min={0}
                                                                        max={selected
                                                                            .get(
                                                                                productID,
                                                                            )?.offers?.offers[0]
                                                                            .inventoryLevel.value}
                                                                        aria-label={(selected
                                                                            .get(productID)
                                                                            ?.quantity ?? 0)
                                                                            .toString()}
                                                                        value={selected
                                                                            .get(productID)
                                                                            ?.quantity ?? 0}
                                                                        onChange={(e) => {
                                                                            const value = Math.min(
                                                                                selectedProducts
                                                                                    .value
                                                                                    .get(
                                                                                        productID,
                                                                                    )?.offers
                                                                                    ?.offers[0]
                                                                                    .inventoryLevel
                                                                                    .value ?? 0,
                                                                                Math.max(
                                                                                    0,
                                                                                    e.currentTarget
                                                                                        .valueAsNumber,
                                                                                ),
                                                                            )

                                                                            e.currentTarget.value =
                                                                                value.toString()

                                                                            updateProductQuantity(
                                                                                productID,
                                                                                value,
                                                                            )
                                                                        }}
                                                                    />
                                                                </div>
                                                                <div className='flex justify-center'>
                                                                    <Button
                                                                        onClick={() => {
                                                                            const input = document
                                                                                .getElementById(
                                                                                    productID,
                                                                                ) as HTMLInputElement
                                                                            const value = Math.min(
                                                                                selectedProducts
                                                                                    .value
                                                                                    .get(
                                                                                        productID,
                                                                                    )?.offers
                                                                                    ?.offers[0]
                                                                                    .inventoryLevel
                                                                                    .value ?? 100,
                                                                                Math.max(
                                                                                    0,
                                                                                    input
                                                                                        .valueAsNumber -
                                                                                        1,
                                                                                ),
                                                                            )

                                                                            input.value = value
                                                                                .toString()

                                                                            updateProductQuantity(
                                                                                productID,
                                                                                value,
                                                                            )
                                                                            restoreBuyBox(url)
                                                                        }}
                                                                        className='btn-xs text-xl bg-white border border-r-0 rounded-none border-neutral-300 h-[31px] w-[31px]'
                                                                    >
                                                                        -
                                                                    </Button>
                                                                    <Button
                                                                        onClick={() => {
                                                                            const input = document
                                                                                .getElementById(
                                                                                    productID,
                                                                                ) as HTMLInputElement
                                                                            const value = Math.min(
                                                                                selectedProducts
                                                                                    .value
                                                                                    .get(
                                                                                        productID,
                                                                                    )?.offers
                                                                                    ?.offers[0]
                                                                                    .inventoryLevel
                                                                                    .value ?? 100,
                                                                                Math.max(
                                                                                    0,
                                                                                    input
                                                                                        .valueAsNumber +
                                                                                        1,
                                                                                ),
                                                                            )

                                                                            input.value = value
                                                                                .toString()

                                                                            updateProductQuantity(
                                                                                productID,
                                                                                value,
                                                                            )
                                                                            restoreBuyBox(url)
                                                                        }}
                                                                        className='btn-xs text-xl bg-white border rounded-none border-neutral-300 h-[31px] w-[31px]'
                                                                    >
                                                                        +
                                                                    </Button>
                                                                </div>
                                                                <div className='h-7'>
                                                                    {quantity < 10 && (
                                                                        <>
                                                                            {quantity === 1
                                                                                ? (
                                                                                    <p className='text-xs text-red-500 text-center font-bold leading-[1.1]'>
                                                                                        Última
                                                                                        unidade
                                                                                    </p>
                                                                                )
                                                                                : (
                                                                                    <>
                                                                                        <p className='text-xs text-red-500 text-center font-bold leading-[1.1]'>
                                                                                            Restam
                                                                                        </p>
                                                                                        <p className='text-xs text-red-500 text-center font-bold leading-[1.1]'>
                                                                                            {quantity}
                                                                                            {' '}
                                                                                            uni 🔥
                                                                                        </p>
                                                                                    </>
                                                                                )}
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )
                                                        : <NoStockButton url={url} />}
                                                </div>
                                            </li>
                                        )
                                    },
                                )}
                        </>
                    ))}
                </ul>
            </div>
            <div className='my-2 bg-pink-light text-lg max-w-[300px] border-l-4 px-[14px] py-[10px] space-y-[5px] border-salmon text-salmon order-1 lg:order-[unset]'>
                <p>
                    <span className='font-bold'>Itens selecionados:</span> {itemsCountValue}
                </p>
                <p>
                    <span className='font-bold'>Subtotal:</span> {formatPrice(subtotalValue)}
                </p>
            </div>
        </>
    )
}
